import { PostStatus } from "@growth/schema";
import { Badge, Button, cn, Input } from "@growth/ui";
import {
  ChevronLeft,
  ChevronRight,
  FilePenLine,
  LoaderCircle,
  Plus,
  Search,
  WandSparkles,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { NoBlog } from "../components/no-blog";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { formatDate, formatRelative } from "../lib/format";
import { useCreatePost, usePostList } from "../lib/posts";

const tabs: { label: string; status?: PostStatus }[] = [
  { label: "All" },
  { label: "Drafts", status: "DRAFT" },
  { label: "Published", status: "PUBLISHED" },
];

const useDebounced = <T,>(value: T, delay: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

export const PostsPage = () => {
  const { brand } = useActive();
  if (brand && !brand.siteId) return <NoBlog />;
  return <PostList brandName={brand?.name ?? ""} />;
};

const PostList = ({ brandName }: { brandName: string }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<PostStatus | undefined>();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const q = useDebounced(search.trim(), 300);

  useEffect(() => setPage(1), [status, q]);

  const posts = usePostList({ status, q: q || undefined, page, pageSize: 20 });
  const createPost = useCreatePost();

  const newPost = () =>
    createPost.mutate({ title: "" }, { onSuccess: (post) => navigate(`/posts/${post.id}`) });

  const totalPages = posts.data
    ? Math.max(1, Math.ceil(posts.data.total / posts.data.pageSize))
    : 1;

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted">{brandName}</p>
          <h1 className="font-serif text-3xl text-ink">Posts</h1>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="secondary">
            <Link to="/ai">
              <WandSparkles className="size-4" />
              Write with AI
            </Link>
          </Button>
          <Button onClick={newPost} disabled={createPost.isPending}>
            {createPost.isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <Plus className="size-4" />
            )}
            New post
          </Button>
        </div>
      </header>

      {createPost.isError && (
        <p role="alert" className="text-sm text-red-600">
          {apiErrorMessage(createPost.error, "Couldn't create the post")}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          role="tablist"
          aria-label="Filter by status"
          className="flex gap-1 rounded-lg bg-paper p-1"
        >
          {tabs.map((tab) => (
            <button
              key={tab.label}
              type="button"
              role="tab"
              aria-selected={status === tab.status}
              onClick={() => setStatus(tab.status)}
              className={cn(
                "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                status === tab.status ? "bg-white text-ink shadow-sm" : "text-muted hover:text-ink",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <Input
            type="search"
            aria-label="Search posts by title"
            placeholder="Search by title"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-line bg-white">
        {posts.isPending ? (
          <div className="flex justify-center py-16" aria-label="Loading posts">
            <LoaderCircle className="size-5 animate-spin text-muted" />
          </div>
        ) : posts.isError ? (
          <p role="alert" className="px-6 py-16 text-center text-sm text-red-600">
            {apiErrorMessage(posts.error, "Couldn't load posts")}
          </p>
        ) : posts.data.items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <FilePenLine className="size-6 text-muted" />
            <p className="text-sm text-muted">
              {q || status
                ? "No posts match these filters."
                : `No ${brandName} posts yet. Start one with New post.`}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-paper/50 text-left text-xs text-muted">
              <tr>
                <th className="px-4 py-2.5 font-medium">Title</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="hidden px-4 py-2.5 font-medium md:table-cell">Author</th>
                <th className="px-4 py-2.5 text-right font-medium">Updated</th>
              </tr>
            </thead>
            <tbody className={cn("divide-y divide-line", posts.isPlaceholderData && "opacity-60")}>
              {posts.data.items.map((post) => (
                <tr key={post.id} className="transition-colors hover:bg-paper/40">
                  <td className="px-4 py-3">
                    <Link
                      to={`/posts/${post.id}`}
                      className="block font-medium text-ink hover:text-accent"
                    >
                      {post.title || <span className="italic text-muted">Untitled</span>}
                    </Link>
                    <span className="text-xs text-muted">/blog/{post.slug}</span>
                  </td>
                  <td className="px-4 py-3">
                    {post.status === "PUBLISHED" ? (
                      <Badge
                        tone="success"
                        title={
                          post.publishedAt ? `Published ${formatDate(post.publishedAt)}` : undefined
                        }
                      >
                        Published
                      </Badge>
                    ) : (
                      <Badge>Draft</Badge>
                    )}
                  </td>
                  <td className="hidden px-4 py-3 text-muted md:table-cell">
                    {post.author?.name ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-muted">
                    {formatRelative(post.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {posts.data && totalPages > 1 && (
        <nav
          aria-label="Pagination"
          className="flex items-center justify-end gap-2 text-xs text-muted"
        >
          <span>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="secondary"
            className="h-8 px-2"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="secondary"
            className="h-8 px-2"
            aria-label="Next page"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            <ChevronRight className="size-4" />
          </Button>
        </nav>
      )}
    </section>
  );
};
